<template>
  <div class="common-table">
    <filter-bar
      :title="table.title"
      :query="table.query"
      :search-data="table.searchData"
      @search-change="doSearch"
    >
      <span slot="before">
        <slot name="filter-before" />
      </span>
      <span slot="after">
        <slot name="filter-after" />
      </span>
      <span slot="action">
        <slot name="filter-action"></slot>
      </span>
    </filter-bar>
    <slot name="table-before"></slot>
    <div class="common-table__content">
      <div class="common-table__table">
        <el-table
          ref="table"
          :header-cell-style="{zoom: '80%'}"
          :cell-style="{zoom: '80%'}"
          :height="table.height"
          :show-header="!table.hideHeader"
          tooltip-effect="dark"
          :stripe="true"
          :data="data"
          :row-key="rowKey"
          v-loading="loading"
          element-loading-text="loading..."
          :empty-text="emptyText"
          :highlight-current-row="true"
          @row-click="handleRowClick"
          @select="handleSelect"
          @select-all="handleSelectAll"
          @selection-change="handleSelectionChange"
          :row-class-name="handleRowSelectClassName"
        >
          <el-table-column
            type="selection"
            width="50"
            v-if="table.hasCheckbox"
            reserve-selection="reserve-selection"
          ></el-table-column>
          <template v-for="(column, idx) in table.columns">
            <el-table-column
              :key="idx"
              v-if="!column.hide"
              :label="column.title"
              :width="column.width"
              :fixed="column.fixed"
              :align="column.align || 'left'"
              :render-header="column.renderHeader"
              :show-overflow-tooltip="column.showTooltip"
            >
              <template slot="header" slot-scope="scope">
                <div
                  class="common-table__table--header"
                  :style="{ cursor: column.sortable ? 'pointer' : 'default' }"
                  @click="doHeaderClick(column)"
                >
                  <span
                    :class="{
                      'sort-label': column.sort && column.sort !== 'none'
                    }"
                    class="common-table__table--title"
                  >
                    <template v-if="column.title.indexOf('#') < 0">
                      <span>{{ column.title }}</span>
                    </template>
                    <template v-else>
                      <div>
                        {{ splitComplexTitle(column.title, 'start') }}
                      </div>
                      <div>
                        {{ splitComplexTitle(column.title, 'end') }}
                      </div>
                    </template>
                  </span>
                  <sortable-caret
                    :sort="column.sort"
                    disabled
                    v-if="column.sortable"
                  ></sortable-caret>
                </div>
              </template>
              <template slot-scope="scope">
                <cell
                  v-if="column.render"
                  :column="column"
                  :row="scope.row"
                  :index="scope.$index"
                  :render="column.render"
                ></cell>
                <span v-else-if="column.filter">
                  {{ $filters[column.filter](scope.row[column.key]) }}
                </span>
                <span v-else-if="column.enumType">
                  {{
                    $enums[column.enumType].getName(scope.row[column.key]) ||
                      '--'
                  }}
                </span>
                <span v-else-if="!column.filter &amp;&amp; !column.enumType">
                  {{
                    [undefined, null, ''].includes(scope.row[column.key])
                      ? '--'
                      : scope.row[column.key]
                  }}
                </span>
              </template>
            </el-table-column>
          </template>
        </el-table>
        <el-pagination
          class="common-table__pagination"
          v-if="table.showPagination"
          :total="pagination.total"
          :current-page="pagination.pageNo"
          :page-size="pagination.pageSize"
          :page-sizes="[5, 10, 25, 50, 100, 200]"
          :layout="paginationLayout"
          @size-change="handlePageSizeChange"
          @current-change="handlePageChange"
        >
        </el-pagination>
      </div>
    </div>
    <slot name="table-after"></slot>
  </div>
</template>

<script>
import filterBar from './filter-bar'
import cell from './cell'
import SortableCaret from '@/components/sortable-caret/index.vue'
import config from '@/config'

const ALL_PAGINATION_LAYOUT = 'total, sizes, prev, pager, next'
const SIMPLE_PAGINATION_LAYOUT = 'total, prev, pager, next'

export default {
  components: {
    filterBar,
    cell,
    SortableCaret
  },
  props: {
    table: {
      type: Object,
      required: true
    },
    emptyText: {
      type: String,
      default: 'No Data'
    },
    loading: {
      type: Boolean,
      default: false
    },
    data: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => {}
    },
    simplePagination: {
      type: Boolean,
      default: false
    },
    selectRow: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      paginationLayout: ALL_PAGINATION_LAYOUT
    }
  },
  watch: {
    simplePagination(val) {
      this.paginationLayout = !val
        ? ALL_PAGINATION_LAYOUT
        : SIMPLE_PAGINATION_LAYOUT
    }
  },
  methods: {
    rowKey(row) {
      return row.id
    },
    doSearch() {
      if (!this.checkStr(this.table.query.keywords)) {
        return this.$message.warning('Illegal characters')
      }
      // this.emitSearch()
      this.handlePageChange(1)
    },
    emitSearch() {
      this.$emit('search')
    },
    handleSelectionChange(val) {
      this.$emit('selection-change', val)
    },
    handleSelect(selection, row) {
      this.$emit('select', selection, row)
    },
    handleSelectAll(selection) {
      this.$emit('select-all', selection)
    },
    clearSelection() {
      this.$refs.table && this.$refs.table.clearSelection()
    },
    toggleRowSelection(row, selected) {
      this.$refs.table && this.$refs.table.toggleRowSelection(row, selected)
    },
    handleRowClick(val) {
      this.$emit('row-click', val)
    },
    handlePageSizeChange(val) {
      this.$emit('page-size-change', val)
    },
    handlePageChange(val) {
      this.$emit('page-change', val)
    },
    doHeaderClick(column) {
      let idx = config.sortableList.findIndex(t => t === column.sort)
      if (idx < 0) {
        return
      }
      let sortBy = config.sortableList[(idx + 1) % config.sortableList.length]
      this.$emit('sort', column.key, sortBy)
    },
    handleRowSelectClassName({ row, rowIndex }) {
      if (
        this.selectRow &&
        this.rowKey(this.selectRow) &&
        this.rowKey(row) === this.rowKey(this.selectRow)
      ) {
        return 'select-row'
      }
      return ''
    },
    checkStr(str) {
      return /(^$)|(^[^@/'\\"#$%&^*]+$)/.test(str)
    },
    splitComplexTitle(src, type) {
      let idx = (src || '').indexOf('#')
      if (idx < 0) {
        return ''
      }
      if (type === 'start') {
        return src.substring(0, idx)
      } else if (type === 'end') {
        return src.substring(idx + 1)
      }
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.common-table {
  .sort-label {
    color: $primary-color;
  }
  &__content {
    @include clearfix;
  }

  &__table {
    // margin-top: $primary-gap;

    &--header {
      cursor: pointer;
      @include hor-start-center;
    }

    &--title {
      text-transform: uppercase;
      display: inline-block;
    }
    .el-button--text {
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  &__pagination {
    margin: $primary-gap 0 0;
    text-align: center;
  }
}
</style>
